import { useState, useEffect, useCallback } from 'react';
import { expenseBreakdownApi, formatCurrency } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Receipt } from 'lucide-react';

const CATEGORY_COLORS = {
  mortgage: '#3B82F6',
  letting_agent_fee: '#8B5CF6',
  insurance: '#F59E0B',
  maintenance: '#10B981',
  council_tax: '#EF4444',
  other: '#6B7280',
};

export const ExpenseBreakdownWidget = ({ selectedPropertyIds = [] }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchBreakdown = useCallback(async () => {
    if (selectedPropertyIds.length === 0) return;
    setLoading(true);
    try {
      const response = await expenseBreakdownApi.get(selectedPropertyIds);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch expense breakdown:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyIds]);

  useEffect(() => {
    fetchBreakdown();
  }, [fetchBreakdown]);

  const maxAmount = data?.items?.length
    ? Math.max(...data.items.map(i => i.amount))
    : 0;

  return (
    <Card className="border-slate-200" data-testid="expense-breakdown-widget">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
            <Receipt className="w-5 h-5 text-slate-500" />
            Monthly Expense Breakdown
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="spinner w-6 h-6"></div>
          </div>
        ) : !data?.items?.length ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Receipt className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No expenses recorded</p>
            <p className="text-xs text-slate-400 mt-1">Add expenses to your properties to see a breakdown</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Category rows */}
            <div className="space-y-3" data-testid="expense-category-list">
              {data.items.map((item) => {
                const barWidth = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0;
                const color = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;
                
                return (
                  <div key={item.category} data-testid={`expense-category-${item.category}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-sm font-medium text-slate-700">
                          {item.label}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900 tabular-nums">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barWidth}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total */}
            <div className="flex items-center justify-between pt-3 border-t border-slate-200">
              <span className="text-sm font-semibold text-slate-900">Total Monthly</span>
              <span className="text-base font-bold text-slate-900" data-testid="expense-total">
                {formatCurrency(data.total)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExpenseBreakdownWidget;
